import React, { useState } from 'react';
import axios from 'axios';
import { useApiClient } from '../../config/axios';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse
} from '@mui/material';
import {
  AutoFixHigh as AIIcon,
  PlayArrow as ExecuteIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';

interface ChangePreview {
  path: string;
  fromValue: any;
  toValue: any;
  viewName?: string;
}

interface QueryResult {
  viewName?: string;
  itemId?: string;
  label?: string;
  renderer?: string;
  dataIndex?: string;
  formula?: string;
  calculation?: string | object;
  extraProps?: string;
  itemCount?: number;
  description?: string;
  // Pivot-specific fields
  pivotName?: string;
  metricName?: string;
  expressionSql?: string;
  formulaLabel?: string;
  // Formula definition fields
  formulaName?: string;
  plainLanguageSummary?: string;
  businessDefinition?: string;
  // Suggestion fields (for fuzzy matching)
  suggestion?: boolean;
  matchScore?: number;
  // Duplicate detection fields
  sourceType?: string;
  sourceName?: string;
  duplicateCount?: number;
  // Duplicate list fields
  dupCount?: number;
  // Customer usage fields
  customerName?: string;
  viewCount?: number;
  pivotCount?: number;
}

interface ProcessingLog {
  message: string;
  type: 'info' | 'success' | 'error';
  timestamp: Date;
}

const MassUpdate: React.FC = () => {
  const apiClient = useApiClient();
  const [prompt, setPrompt] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewChanges, setPreviewChanges] = useState<ChangePreview[]>([]);
  const [queryResults, setQueryResults] = useState<QueryResult[]>([]);
  const [isQueryMode, setIsQueryMode] = useState(false);
  const [resultType, setResultType] = useState<string>('generic');
  const [processingLogs, setProcessingLogs] = useState<ProcessingLog[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [showLogs, setShowLogs] = useState(true);
  const [displayFieldType, setDisplayFieldType] = useState<'formula' | 'calculation'>('calculation');

  // Formula addition state
  const [showTypeSelection, setShowTypeSelection] = useState(false);
  const [showUseSelection, setShowUseSelection] = useState(false);
  const [formulaData, setFormulaData] = useState<any>(null);
  const [pendingFormulaData, setPendingFormulaData] = useState<any>(null);

  // Help dialog state
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [userGuideContent, setUserGuideContent] = useState('');
  const [loadingGuide, setLoadingGuide] = useState(false);

  const loadUserGuide = async () => {
    if (userGuideContent) return; // Already loaded

    setLoadingGuide(true);
    try {
      const response = await apiClient.get('/user-guide');
      if (response.data.success) {
        setUserGuideContent(response.data.content);
      }
    } catch (error) {
      console.error('Failed to load user guide:', error);
      setUserGuideContent('Failed to load user guide. Please try again.');
    } finally {
      setLoadingGuide(false);
    }
  };

  const addLog = (message: string, type: 'info' | 'success' | 'error' = 'info') => {
    setProcessingLogs(prev => [...prev, { message, type, timestamp: new Date() }]);
  };

  // Helper function to determine what field type the query is looking for
  const detectQueryFieldType = (queryPrompt: string): 'formula' | 'calculation' => {
    const prompt = queryPrompt.toLowerCase();

    // Check for explicit formula keywords
    if (prompt.includes('formula for') ||
      prompt.includes('formula of') ||
      prompt.includes('show formula') ||
      prompt.includes('what formula') ||
      prompt.includes('find formula')) {
      return 'formula';
    }

    // Check for explicit calculation keywords
    if (prompt.includes('calculation for') ||
      prompt.includes('calculation of') ||
      prompt.includes('show calculation') ||
      prompt.includes('what calculation') ||
      prompt.includes('find calculation')) {
      return 'calculation';
    }

    // Default to calculation if ambiguous
    return 'calculation';
  };

  const handlePreviewChanges = async () => {
    if (!prompt.trim()) {
      addLog('Please describe what you want to change', 'error');
      return;
    }

    setIsProcessing(true);
    setProcessingLogs([]);
    setShowLogs(true);
    setPreviewChanges([]);

    try {
      addLog('🤖 Starting AI analysis...');
      addLog('📊 Loading configuration data...');

      // Detect what field type the user is searching for
      const fieldType = detectQueryFieldType(prompt.trim());
      setDisplayFieldType(fieldType);

      const response = await apiClient.post('/mass-update/analyze', {
        prompt: prompt.trim(),
        targetType: 'views'
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        addLog('✅ Processing completed successfully');

        if (response.data.queryType === 'read') {
          // Handle query results
          const { analysis, results, summary, resultType } = response.data;
          addLog(`🔍 Query Results: ${analysis}`);
          setQueryResults(results || []);
          setIsQueryMode(true);
          setResultType(resultType || 'generic');
          setPreviewChanges([]);
        } else if (response.data.queryType === 'add_formula_type_selection') {
          // Handle formula type selection
          addLog(`📋 ${response.data.message}`);
          setFormulaData(response.data.formulaData);
          setShowTypeSelection(true);
          return; // Don't show preview, show selection dialog
        } else if (response.data.queryType === 'add_formula_use_selection') {
          // Handle formula use selection
          addLog(`📋 ${response.data.message}`);
          setPendingFormulaData(response.data.formulaData);
          setShowUseSelection(true);
          return; // Don't show preview, show selection dialog
        } else if (response.data.queryType === 'add_formula_success') {
          // Handle successful formula addition
          addLog(`✅ ${response.data.message}`, 'success');
          addLog(`📝 ${response.data.analysis}`);
          setQueryResults([response.data.formulaData]);
          setIsQueryMode(true);
          setResultType('catalog');
          setPreviewChanges([]);
        } else {
          // Handle update results
          const { analysis, changes, summary } = response.data;
          addLog(`✅ Found ${changes.length} changes to apply`);
          if (analysis) {
            addLog(`Update Plan: ${analysis}`);
          }
          setPreviewChanges(changes);
          setQueryResults([]);
          setIsQueryMode(false);
        }

        setShowPreview(true);
      } else {
        throw new Error(response.data.error || 'Analysis failed');
      }

    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.details || error.response?.data?.error || error.message
        : String(error);
      addLog(`❌ Error during analysis: ${errorMessage}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExecuteChanges = async () => {
    setIsExecuting(true);
    setExecutionProgress(0);
    addLog('🚀 Starting execution...');

    try {
      const response = await apiClient.post('/mass-update/execute', {
        changes: previewChanges
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        const { appliedChanges, errors, summary } = response.data;

        // Simulate progress updates for visual feedback
        for (let i = 0; i < appliedChanges.length; i++) {
          const change = appliedChanges[i];
          addLog(`🔄 Updating ${change.path}...`);
          await new Promise(resolve => setTimeout(resolve, 200)); // Small delay for visual feedback
          setExecutionProgress(((i + 1) / previewChanges.length) * 100);
          addLog(`✅ Updated ${change.viewName || change.path}`);
        }

        // Report any errors
        errors.forEach((error: string) => {
          addLog(`⚠️ ${error}`, 'error');
        });

        addLog('💾 Saving changes to database...');
        addLog(`✅ Successfully applied ${summary.successful}/${summary.totalAttempted} changes!`, 'success');

        if (summary.failed > 0) {
          addLog(`⚠️ ${summary.failed} changes failed - see errors above`, 'error');
        }

      } else {
        throw new Error(response.data.error || 'Execution failed');
      }

    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.details || error.response?.data?.error || error.message
        : String(error);
      addLog(`❌ Error during execution: ${errorMessage}`, 'error');
    } finally {
      setIsExecuting(false);
      setExecutionProgress(100);
    }
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewChanges([]);
    setIsExecuting(false);
    setExecutionProgress(0);
  };

  // Handle formula type selection
  const handleTypeSelection = async (selectedType: string) => {
    setShowTypeSelection(false);
    addLog(`📝 Selected type: ${selectedType}`);

    if (!formulaData) return;

    // Create new prompt with the selected type
    const newPrompt = `add formula ${formulaData.name} with description "${formulaData.description}" and definition "${formulaData.definition}" type ${selectedType}`;

    try {
      const response = await apiClient.post('/mass-update/analyze', {
        prompt: newPrompt,
        targetType: 'views'
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        if (response.data.queryType === 'add_formula_use_selection') {
          // Need use selection for formula type
          addLog(`📋 ${response.data.message}`);
          setPendingFormulaData(response.data.formulaData);
          setShowUseSelection(true);
        } else if (response.data.queryType === 'add_formula_success') {
          // Formula added successfully
          addLog(`✅ ${response.data.message}`, 'success');
          addLog(`📝 ${response.data.analysis}`);
          setQueryResults([response.data.formulaData]);
          setIsQueryMode(true);
          setResultType('catalog');
          setPreviewChanges([]);
          setShowPreview(true);
        }
      } else {
        addLog(`❌ Error: ${response.data.error}`, 'error');
      }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.details || error.response?.data?.error || error.message
        : String(error);
      addLog(`❌ Error during type selection: ${errorMessage}`, 'error');
    }
  };

  // Handle formula use selection
  const handleUseSelection = async (selectedUse: string) => {
    setShowUseSelection(false);
    addLog(`📝 Selected use: ${selectedUse}`);

    if (!pendingFormulaData) return;

    // Create new prompt with the selected use
    const newPrompt = `add formula ${pendingFormulaData.name} with description "${pendingFormulaData.description}" and definition "${pendingFormulaData.definition}" type ${pendingFormulaData.type} use ${selectedUse}`;

    try {
      const response = await apiClient.post('/mass-update/analyze', {
        prompt: newPrompt,
        targetType: 'views'
      }, {
        withCredentials: true
      });

      if (response.data.success && response.data.queryType === 'add_formula_success') {
        addLog(`✅ ${response.data.message}`, 'success');
        addLog(`📝 ${response.data.analysis}`);
        setQueryResults([response.data.formulaData]);
        setIsQueryMode(true);
        setResultType('catalog');
        setPreviewChanges([]);
        setShowPreview(true);
      } else {
        addLog(`❌ Error: ${response.data.error}`, 'error');
      }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.details || error.response?.data?.error || error.message
        : String(error);
      addLog(`❌ Error during use selection: ${errorMessage}`, 'error');
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Paper elevation={1} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AIIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            AI Assistant
          </Typography>
          <IconButton
            onClick={() => {
              setShowHelpDialog(true);
              loadUserGuide();
            }}
            color="primary"
            sx={{ ml: 2 }}
            title="View User Guide"
          >
            <HelpIcon />
          </IconButton>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Use natural language to manage configurations. AI will answer your questions, make changes, and help you create formulas.
        </Typography>


        {/* Prompt Input */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Manage with AI
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Examples: • Change all 'Label' fields from 'TY Sales U' to 'TY Sales Units' for all views • Show me the formula for 'Unit Sales' • Add formula sales_ratio with description 'Sales ratio calculation' and definition 'current_sales / target_sales'"
            variant="outlined"
            disabled={isProcessing || isExecuting}
          />
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <Button
            variant="contained"
            startIcon={<ExecuteIcon />}
            onClick={handlePreviewChanges}
            disabled={!prompt.trim() || isProcessing || isExecuting}
          >
            Execute
          </Button>

          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => {
              setPrompt('');
              setProcessingLogs([]);
              setShowLogs(false);
            }}
            disabled={isProcessing || isExecuting}
          >
            Clear
          </Button>
        </Box>

        {/* Processing Logs */}
        {processingLogs.length > 0 && (
          <Paper variant="outlined" sx={{ mb: 4, overflow: 'hidden' }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              backgroundColor: 'grey.50',
              cursor: 'pointer'
            }} onClick={() => setShowLogs(!showLogs)}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Processing Log
              </Typography>
              <IconButton size="small">
                {showLogs ? <CollapseIcon /> : <ExpandIcon />}
              </IconButton>
            </Box>

            <Collapse in={showLogs}>
              <Box sx={{ p: 2, maxHeight: 300, overflowY: 'auto' }}>
                {processingLogs.map((log, index) => (
                  <Box key={index} sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 1,
                    fontSize: '0.875rem',
                    fontFamily: 'monospace'
                  }}>
                    <Typography
                      variant="caption"
                      sx={{
                        mr: 2,
                        minWidth: 60,
                        color: 'text.secondary',
                        fontSize: '0.75rem'
                      }}
                    >
                      {log.timestamp.toLocaleTimeString()}
                    </Typography>
                    <Typography
                      sx={{
                        color: log.type === 'error' ? 'error.main' :
                          log.type === 'success' ? 'success.main' : 'text.primary'
                      }}
                    >
                      {log.message}
                    </Typography>
                  </Box>
                ))}
                {isProcessing && (
                  <LinearProgress sx={{ mt: 2 }} />
                )}
              </Box>
            </Collapse>
          </Paper>
        )}

      </Paper>

      {/* Preview Changes Dialog */}
      <Dialog
        open={showPreview}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: 500 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <ExecuteIcon sx={{ mr: 2 }} />
          {executionProgress >= 100 ? 'Changes Applied Successfully' :
            isExecuting ? 'Applying Changes...' : 'Preview'}
          {isExecuting && (
            <Chip
              label={`${Math.round(executionProgress)}% Complete`}
              color="primary"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
          {executionProgress >= 100 && (
            <Chip
              label="✓ Complete"
              color="success"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>

        <DialogContent>
          {isQueryMode ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              {queryResults.length} result{queryResults.length !== 1 ? 's' : ''} found
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              {previewChanges.length} field{previewChanges.length !== 1 ? 's' : ''} will be updated
            </Alert>
          )}

          {isExecuting && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Progress: {Math.round(executionProgress)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={executionProgress}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {isQueryMode ? (
                    resultType === 'suggestion' ? (
                      <>
                        <TableCell>Label</TableCell>
                        <TableCell>Data Index</TableCell>
                        <TableCell>Match Score</TableCell>
                      </>
                    ) : resultType === 'pivot' ? (
                      <>
                        <TableCell>Pivot Name</TableCell>
                        <TableCell>Metric Name</TableCell>
                        <TableCell>Formula Label</TableCell>
                        <TableCell>Expression SQL</TableCell>
                        <TableCell>Data Index</TableCell>
                      </>
                    ) : resultType === 'catalog' ? (
                      <>
                        <TableCell>Data Index</TableCell>
                        <TableCell>Label</TableCell>
                        <TableCell>Formula</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Calculation</TableCell>
                        <TableCell>Formula Name</TableCell>
                        <TableCell>Plain Language Summary</TableCell>
                        <TableCell>Business Definition</TableCell>
                      </>
                    ) : resultType === 'duplicateList' ? (
                      <>
                        <TableCell>Data Index</TableCell>
                        <TableCell>Label</TableCell>
                        <TableCell>Formula Name</TableCell>
                        <TableCell>Duplicate Count</TableCell>
                      </>
                    ) : resultType === 'duplicate' ? (
                      <>
                        <TableCell>Source Type</TableCell>
                        <TableCell>Source Name</TableCell>
                        <TableCell>Label</TableCell>
                        <TableCell>Renderer</TableCell>
                        <TableCell>Data Index</TableCell>
                        <TableCell>Formula</TableCell>
                        <TableCell>Duplicate Count</TableCell>
                      </>
                    ) : resultType === 'customerUsage' ? (
                      <>
                        <TableCell>Customer Name</TableCell>
                        <TableCell>View Count</TableCell>
                        <TableCell>Pivot Count</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>View Name</TableCell>
                        <TableCell>Label</TableCell>
                        <TableCell>Renderer</TableCell>
                        <TableCell>Data Index</TableCell>
                        <TableCell>Formula</TableCell>
                        <TableCell>Extra Props</TableCell>
                      </>
                    )
                  ) : (
                    <>
                      <TableCell>View Name</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Status</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {isQueryMode ? (
                  queryResults.map((result, index) => (
                    <TableRow key={index}>
                      {resultType === 'suggestion' ? (
                        <>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {result.label}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {result.dataIndex}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={result.matchScore ? `${(result.matchScore * 100).toFixed(0)}%` : 'N/A'}
                              color={result.matchScore && result.matchScore > 0.7 ? 'success' : result.matchScore && result.matchScore > 0.5 ? 'warning' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </>
                      ) : resultType === 'pivot' ? (
                        <>
                          <TableCell>{result.pivotName}</TableCell>
                          <TableCell>{result.metricName}</TableCell>
                          <TableCell>{result.formulaLabel}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {result.expressionSql || ''}
                            </Typography>
                          </TableCell>
                          <TableCell>{result.dataIndex}</TableCell>
                        </>
                      ) : resultType === 'catalog' ? (
                        <>
                          <TableCell>{result.dataIndex}</TableCell>
                          <TableCell>{result.label}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                maxWidth: 250,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {result.formula || ''}
                            </Typography>
                          </TableCell>
                          <TableCell>{result.description || ''}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {typeof result.calculation === 'string' ? result.calculation : JSON.stringify(result.calculation || '')}
                            </Typography>
                          </TableCell>
                          <TableCell>{result.formulaName || ''}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {result.plainLanguageSummary || ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {result.businessDefinition || ''}
                            </Typography>
                          </TableCell>
                        </>
                      ) : resultType === 'duplicateList' ? (
                        <>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}>
                              {result.dataIndex}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {result.label || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {result.formulaName || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${result.dupCount || 0}x`}
                              color="warning"
                              size="small"
                            />
                          </TableCell>
                        </>
                      ) : resultType === 'duplicate' ? (
                        <>
                          <TableCell>
                            <Chip
                              label={result.sourceType || 'Unknown'}
                              color={result.sourceType === 'View' ? 'primary' : 'secondary'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{result.sourceName}</TableCell>
                          <TableCell>{result.label}</TableCell>
                          <TableCell>{result.renderer || '-'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {result.dataIndex}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                maxWidth: 250,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {result.formula || ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${result.duplicateCount || 0}x`}
                              color="warning"
                              size="small"
                            />
                          </TableCell>
                        </>
                      ) : resultType === 'customerUsage' ? (
                        <>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {result.customerName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={result.viewCount || 0}
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={result.pivotCount || 0}
                              color="secondary"
                              size="small"
                            />
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{result.viewName}</TableCell>
                          <TableCell>{result.label}</TableCell>
                          <TableCell>{result.renderer}</TableCell>
                          <TableCell>{result.dataIndex}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {result.formula || ''}
                            </Typography>
                          </TableCell>
                          <TableCell>{result.extraProps}</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                ) : (
                  previewChanges.map((change, index) => (
                    <TableRow key={index}>
                      <TableCell>{change.viewName || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={String(change.fromValue)}
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={String(change.toValue)}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        {isExecuting && index < (executionProgress / 100) * previewChanges.length ? (
                          <CheckIcon color="success" fontSize="small" />
                        ) : isExecuting ? (
                          <Box sx={{ width: 60 }}>
                            <LinearProgress />
                          </Box>
                        ) : (
                          <Chip label="Pending" size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClosePreview}
            disabled={isExecuting}
          >
            Ok
          </Button>
          {executionProgress >= 100 ? (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  // Refresh the page or reload data
                  window.location.reload();
                }}
              >
                Refresh Page
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleClosePreview}
              >
                Done
              </Button>
            </>
          ) : !isQueryMode && !isExecuting ? (
            <Button
              variant="contained"
              startIcon={<ExecuteIcon />}
              onClick={handleExecuteChanges}
              color="primary"
            >
              Apply Changes
            </Button>
          ) : !isQueryMode && isExecuting ? (
            <Button
              variant="outlined"
              onClick={() => {
                setIsExecuting(false);
                setExecutionProgress(0);
              }}
              color="error"
            >
              Stop Execution
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      {/* Type Selection Dialog */}
      <Dialog
        open={showTypeSelection}
        onClose={() => setShowTypeSelection(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Formula Type</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Please select the type for formula '{formulaData?.name}':
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {['base_metrics', 'computed_metric', 'formula'].map((type) => (
              <Button
                key={type}
                variant="outlined"
                onClick={() => handleTypeSelection(type)}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  py: 2
                }}
              >
                <Box>
                  <Typography variant="h6">{type}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {type === 'base_metrics' && 'Basic metric definitions'}
                    {type === 'computed_metric' && 'Calculated metrics based on other metrics'}
                    {type === 'formula' && 'Complex formulas for specific use cases'}
                  </Typography>
                </Box>
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTypeSelection(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Use Selection Dialog */}
      <Dialog
        open={showUseSelection}
        onClose={() => setShowUseSelection(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Formula Use</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Please select the intended use for formula '{pendingFormulaData?.name}':
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {['Pivot', 'View Use'].map((use) => (
              <Button
                key={use}
                variant="outlined"
                onClick={() => handleUseSelection(use)}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  py: 2
                }}
              >
                <Box>
                  <Typography variant="h6">{use}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {use === 'Pivot' && 'For use in pivot tables and reporting'}
                    {use === 'View Use' && 'For use in view configurations and UI'}
                  </Typography>
                </Box>
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUseSelection(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* User Guide Dialog */}
      <Dialog
        open={showHelpDialog}
        onClose={() => setShowHelpDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HelpIcon sx={{ mr: 1, color: 'primary.main' }} />
            AI Assistant User Guide
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingGuide ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <LinearProgress sx={{ width: '50%' }} />
            </Box>
          ) : (
            <Box
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                m: 0,
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                lineHeight: 1.6
              }}
            >
              {userGuideContent}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHelpDialog(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MassUpdate;